import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { ROUTES } from '@app/router/routes';
import { getVowDescribe } from '../api/profileService';
import CloseButton from '@/shared/components/CloseButton';
import { useLanguage } from '@shared/contexts/LanguageContext';

// ─── Lamp image map by category ─────────────────────────────────────────────
const VOW_LAMP_IMG_MAP = {
  '身體健康': '/image/lamp/yellow_x2.png',
  '家庭和樂': '/image/lamp/orange_x2.png',
  '道業成就': '/image/lamp/red_x2.png',
  '學業進步': '/image/lamp/green_x2.png',
  '事業順利': '/image/lamp/blue_x2.png',
  '廣結善緣': '/image/lamp/purple_x2.png',
};

// ─── Single record card ───────────────────────────────────────────────────────
function VowRecordCard({ item, categoryName, onClick }) {
  const { t } = useLanguage();
  const lampSrc = VOW_LAMP_IMG_MAP[categoryName] || VOW_LAMP_IMG_MAP['身體健康'];
  return (
    <div
      className="self-stretch px-2.5 py-2 bg-gradient-to-br from-orange-100/80 via-white to-orange-100/60 rounded-lg outline outline-1 outline-offset-[-1px] outline-[#6E625B]/10 flex flex-row justify-start items-start gap-8 cursor-pointer hover:bg-orange-50/50 transition-colors"
      onClick={onClick}
    >
      {/* Row 1: lamp icon + category + count */}
      <div className="w-6 h-6 relative overflow-hidden flex-shrink-0">
        <img className="w-6 h-6 left-0 top-0 absolute object-contain" src={lampSrc} alt={categoryName} />
      </div>
      {/* Row 2: monastery + user + offer type */}
      <div className="flex-1 grid grid-cols-3 gap-1 items-center w-full">
        <div className="text-[#C59B5A] text-sm font-bold font-['Noto_Sans_TC'] leading-6 text-left truncate" title={item?.monastery_name ?? t('vow-list.noValue')}>
          {item?.monastery_name ?? t('vow-list.noValue')}
        </div>
        <div className="text-[#C59B5A] text-sm font-bold font-['Noto_Sans_TC'] leading-6 text-left truncate" title={item.vower_name}>
          {item.vower_name}
        </div>
        <div className="text-[#C59B5A] text-sm font-bold font-['Noto_Sans_TC'] leading-6 text-right whitespace-nowrap">
          {t('lamp-bulletin.offering')}
        </div>
      </div>
    </div>
  );
}

// ─── Search bar ───────────────────────────────────────────────────────────────
function SearchBar({ value, onChange, onSearch }) {
  const { t } = useLanguage();
  return (
    <div className="self-stretch h-12 bg-white/75 inline-flex justify-end items-center gap-2 overflow-hidden flex-shrink-0">
      <div className="relative flex-1">
        <input
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && onSearch()}
          placeholder="Search"
          className="w-full h-8 bg-white rounded-[30px] border border-[#6E625B]/10 pl-3 pr-8 text-base font-light font-['Noto_Sans_TC'] text-[#6E625B] placeholder-[#6E625B]/40 focus:outline-none focus:border-[#C59B5A]/40 transition-colors"
        />
      </div>
      <button
        onClick={onSearch}
        className="w-7 h-7 flex-shrink-0 flex items-center justify-center hover:opacity-70 transition-opacity"
        aria-label={t('vow-list.searchAria')}
      >
        {/* Search icon */}
        <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
          <circle cx="8.5" cy="8.5" r="5.75" stroke="#6E625B" strokeWidth="1.5" />
          <path d="M13 13L17 17" stroke="#6E625B" strokeWidth="1.5" strokeLinecap="round" />
        </svg>
      </button>
    </div>
  );
}

// ─── VowList popup component ──────────────────────────────────────────────────
/**
 * @param {Object}  props
 * @param {boolean} props.isOpen          - whether the popup is visible
 * @param {Function} props.onClose        - callback to close
 * @param {string|number} props.contentId - the content_id to query
 * @param {string}  props.categoryName    - display label (e.g. "身體健康")
 */
function VowList({ isOpen, onClose, contentId, categoryName }) {
  const { t } = useLanguage();
  const [keyword, setKeyword] = useState('');
  const [activeKeyword, setActiveKeyword] = useState('');
  const [page, setPage] = useState(1);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const listRef = useRef(null);
  const navigate = useNavigate();

  const PER_PAGE = 10;

  // Reset and fetch when popup opens / contentId changes
  useEffect(() => {
    if (!isOpen || contentId == null) return;
    setKeyword('');
    setActiveKeyword('');
    setPage(1);
    setItems([]);
    setHasMore(false);
    fetchPage(1, '');
  }, [isOpen, contentId]);

  const fetchPage = useCallback(
    async (targetPage, kw) => {
      if (targetPage === 1) setLoading(true);
      else setLoadingMore(true);

      try {
        const res = await getVowDescribe({
          page: targetPage,
          per_page: PER_PAGE,
          keyword: kw,
          content_id: contentId,
        });
        const newItems = res?.items ?? [];
        const totalCount = res?.total ?? newItems.length;

        setItems((prev) => (targetPage === 1 ? newItems : [...prev, ...newItems]));
        setTotal(totalCount);
        setHasMore(targetPage * PER_PAGE < totalCount);
        setPage(targetPage);
      } catch (err) {
        console.error('VowList fetch error:', err);
        if (targetPage === 1) setItems([]);
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    },
    [contentId]
  );

  const handleSearch = useCallback(() => {
    setActiveKeyword(keyword);
    setPage(1);
    setItems([]);
    fetchPage(1, keyword);
  }, [keyword, fetchPage]);

  useEffect(() => {
    if (keyword === '' && activeKeyword !== '') {
      const timer = setTimeout(() => {
        handleSearch();
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [keyword, activeKeyword, handleSearch]);

  // Close on backdrop click
  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) onClose();
  };

  if (!isOpen) return null;

  return (
    // Backdrop
    <div
      className="fixed inset-0 z-100 flex items-center justify-center p-4 sm:p-6"
      style={{ background: 'rgba(0,0,0,0.45)', backdropFilter: 'blur(2px)' }}
      onClick={handleBackdropClick}
    >
      {/* Card */}
      <div
        className="
          relative bg-white rounded-[20px] flex flex-col gap-4 overflow-hidden
          w-full max-w-sm
          h-[90vh] max-h-[785px]
          p-6
          shadow-[0px_11px_23px_0px_rgba(0,0,0,0.10),0px_42px_42px_0px_rgba(0,0,0,0.09),0px_95px_57px_0px_rgba(0,0,0,0.05)]
        "
        onClick={(e) => e.stopPropagation()}
      >
        {/* ── Header ── */}
        <div className="flex items-baseline justify-between flex-shrink-0">
          <div className="text-[#171717]/95 text-xl sm:text-2xl font-bold font-['Noto_Serif_TC'] leading-7">
            {t('vow-list.header')}
            {categoryName && (
              <span className="text-[#BC313D]"> {categoryName}</span>
            )}
          </div>
          {/* Total count hint */}
          {items.length > 0 && (
            <p className="text-center text-[#6E625B]/70 text-xs font-['Noto_Sans_TC']">
              {t('vow-list.totalRecords').replace('{{count}}', total)}
            </p>
          )}

          {/* Close button */}
          <button
            onClick={onClose}
            aria-label={t('vow-list.closeAria')}
            className="w-6 h-6 flex items-center justify-center flex-shrink-0 hover:opacity-60 transition-opacity"
          >
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
              <circle cx="10" cy="10" r="9.25" stroke="rgba(110,98,91,0.30)" strokeWidth="1.5" />
              <path d="M7 7L13 13M13 7L7 13" stroke="rgba(110,98,91,0.30)" strokeWidth="1.5" strokeLinecap="round" />
            </svg>
          </button>
        </div>

        {/* ── Search ── */}
        <SearchBar
          value={keyword}
          onChange={setKeyword}
          onSearch={handleSearch}
        />

        {/* ── List ── */}
        <div ref={listRef} className="flex-1 overflow-y-auto -mx-1 px-1">
          {loading ? (
            <div className="flex flex-col gap-2">
              {[...Array(5)].map((_, i) => (
                <div
                  key={i}
                  className="h-[68px] rounded-lg bg-orange-50/60 animate-pulse"
                />
              ))}
            </div>
          ) : items.length === 0 ? (
            <div className="flex flex-col items-center justify-center gap-2 py-16">
              <svg width="40" height="40" viewBox="0 0 40 40" fill="none" opacity="0.3">
                <circle cx="18" cy="18" r="11.5" stroke="#6E625B" strokeWidth="2" />
                <path d="M27 27L35 35" stroke="#6E625B" strokeWidth="2" strokeLinecap="round" />
              </svg>
              <p className="text-[#6E625B]/40 text-sm font-['Noto_Sans_TC']">
                {activeKeyword ? t('vow-list.noResultsKeyword').replace('{{keyword}}', activeKeyword) : t('vow-list.noRecords')}
              </p>
            </div>
          ) : (
            <div className="flex flex-col gap-2">
              {items.map((item, idx) => (
                <VowRecordCard
                  key={item.record_id ?? idx}
                  item={item}
                  categoryName={categoryName}
                  onClick={() => navigate(ROUTES.BLESSING_RESULT, { state: { recordId: item.record_id } })}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default VowList;
